import { IApiSmpPromptType } from "@smp/api";
import { RequireAtLeastOne } from "@smp/utils";
import { ReactNode } from "react";
import StringSchema from "yup/lib/string";

import { GPT_LANGUAGES, GPT_TEXT_STYLES } from "@/fixtures";
import { EPromptKey } from "@/hooks/use_prompt_fetcher";

export enum EAiCardTag {
  IDEA = "idea",
  TEXT = "text",
  POST = "post",
  STORY = "story",
  IMAGE = "image",
  PLANNING = "planning",
  VIDEO = "video",
  TOOL = "tool",
}

interface AiCardFieldBase<
  Component extends
    | "input"
    | "select"
    | "style_select"
    | "language_select"
    | "length_select"
    | "period_select"
    | "weekday_select"
    | "range"
    | "image_describe",
> {
  id: string;
  component: Component;
  isPrimaryField?: boolean;
}

interface AiCardFieldInput extends AiCardFieldBase<"input"> {
  componentProps?: Partial<{
    minRows: number;
    maxRows: number;
    title: ReactNode | string;
    placeholder: string;
    validation: StringSchema;
    isCollapsible: boolean;
  }>;
}

interface AiCardFieldSelect extends AiCardFieldBase<"select"> {
  componentProps: {
    title?: ReactNode | string;
    placeholder?: string;
    options: Array<{
      value: string;
      label: string;
    }>;
    sortFunction?: (a: string, b: string) => number;
  };
}

interface AiCardFieldStyleSelect extends AiCardFieldBase<"style_select"> {
  componentProps?: Partial<{
    default: typeof GPT_TEXT_STYLES[number]["id"];
  }>;
}

interface AiCardFieldLanguageSelect extends AiCardFieldBase<"language_select"> {
  componentProps?: Partial<{
    default: typeof GPT_LANGUAGES[number]["id"];
  }>;
}

interface AiCardFieldLengthSelect extends AiCardFieldBase<"length_select"> {
  componentProps: {
    title?: ReactNode | string;
    options: string[];
  };
}

interface AiCardFieldPeriodSelect extends AiCardFieldBase<"period_select"> {}

interface AiCardFieldWeekdaySelect extends AiCardFieldBase<"weekday_select"> {}

interface AiCardFieldRange extends AiCardFieldBase<"range"> {
  componentProps: {
    min: number;
    max: number;
    default?: number;
    step?: number;
    title?: ReactNode | string;
  };
}

interface AiCardFieldImageDescribe extends AiCardFieldBase<"image_describe"> {
  componentProps?: Partial<{
    title: ReactNode | string;
    placeholder: string;
    validation: StringSchema;
  }>;
}

export type AiCardField =
  | AiCardFieldInput
  | AiCardFieldSelect
  | AiCardFieldStyleSelect
  | AiCardFieldLanguageSelect
  | AiCardFieldLengthSelect
  | AiCardFieldPeriodSelect
  | AiCardFieldWeekdaySelect
  | AiCardFieldRange
  | AiCardFieldImageDescribe;

export type AiCard = RequireAtLeastOne<
  {
    id: IApiSmpPromptType;
    tags: EAiCardTag[];
    title: ReactNode | string;
    description: ReactNode | string;
    icon: ReactNode;
    askButtonText?: ReactNode | string;
    fields?: AiCardField[][];
    prompt_fetcher_key: EPromptKey;
    prompt_generator: (
      values: Record<AiCardField["id"], string | string[]>,
    ) => string;
    isPublicationAllowed?: boolean;
  },
  "prompt_generator" | "prompt_fetcher_key"
>;
